import React, { useState, useEffect, useMemo } from "react";
import "../../styles/createAppointmentStyles.css";
import { Button } from "react-md";
import { useRefundDialog } from "./useRefundDialog";
import { RefundDialog } from "./RefundDialog";
import PaymentLinkDialog from "../payFromBridge/PaymentLinkDialog";
import { getPaymentDisplayState } from "../../helper/paymentViewModel";
import { formatCurrency } from "../../helper/formatCurrency";
import RefreshButton from "../../helper/RefreshButton";
import NavigoDashboardUtils from "../../services/NavigoDashboardUtils";
import GlobalSkeletonLoader from "../../../../../../components/common/GlobalSkeletonLoader";

// Who initiated a refund (refundInfo.createdBy). 1/2/4 need a name lookup,
// 3/5 are fixed labels.
const REFUND_CREATED_BY = Object.freeze({
  PATIENT: 1,
  EMPLOYEE: 2,
  SYSTEM: 3,
  EXPERT: 4,
  SERVICE_PROVIDER: 5,
});

const cardStyle = {
  width: "100%",
  margin: "36px 0",
  border: "2px solid #D9D9D9",
  borderRadius: "16px",
  padding: "32px",
  boxSizing: "border-box",
};
const headingStyle = { fontWeight: 700, fontSize: "24px", color: "#000000" };
const labelStyle = {
  fontWeight: 400,
  fontSize: "11px",
  color: "rgb(73, 73, 73)",
  marginBottom: "4px",
};
const valueStyle = { fontWeight: 600, fontSize: "16px", color: "#000000" };
// max-content columns size themselves to the widest row, which keeps the status
// column aligned between the payment and refund rows.
const amountsGridStyle = {
  display: "grid",
  gridTemplateColumns: "max-content max-content auto",
  columnGap: "48px",
  rowGap: "24px",
  alignItems: "center",
  marginTop: "24px",
};
// Cells carry only their right/bottom rule; the wrapper draws the outer border,
// so every line stays a single 1px #D9D9D9.
const cellStyle = {
  padding: "14px 16px",
  textAlign: "left",
  fontSize: "12px",
  color: "#000000",
};
const headerCellStyle = {
  ...cellStyle,
  backgroundColor: "#F6F6F6",
  color: "#757575",
  fontWeight: 500,
  whiteSpace: "nowrap",
};
const tableWrapperStyle = {
  marginTop: "24px",
  border: "1px solid #D9D9D9",
  borderRadius: "8px",
  overflow: "hidden",
};

const TABLE_HEADERS = [
  "Payment method",
  "Amount",
  "Order ID/Refund ID",
  "Payment ID",
  "Transaction date & time",
  "Refund initiated by",
  "Status",
];

const PaymentAndRefund = ({
  appointment,
  fetchAppointment,
  resetDashboardReload,
  profileDetails,
  onRefresh,
  loaderToCheckPaymentStatus,
}) => {
  const { dialogState, setDialogState, openRefundDialog, handleClose } =
    useRefundDialog();
  const [showPaymentLinkDialog, setShowPaymentLinkDialog] = useState(false);
  // Resolved name of whoever initiated the refund; "-" until the lookup lands.
  const [refundInitiatedBy, setRefundInitiatedBy] = useState("-");

  const navigoDashboardUtils = useMemo(() => new NavigoDashboardUtils(), []);

  const view = useMemo(
    () => getPaymentDisplayState(appointment),
    [appointment],
  );

  useEffect(() => {
    if (view.isPending) {
      setShowPaymentLinkDialog(true);
    }
  }, []);

  const { refundCreatedBy, refundCreatedById } = view;
  useEffect(() => {
    if (refundCreatedBy == null) {
      setRefundInitiatedBy("-");
      return;
    }
    if (refundCreatedBy === REFUND_CREATED_BY.SYSTEM) {
      setRefundInitiatedBy("System generated");
      return;
    }
    if (refundCreatedBy === REFUND_CREATED_BY.SERVICE_PROVIDER) {
      setRefundInitiatedBy("Service provider");
      return;
    }
    if (!refundCreatedById) {
      setRefundInitiatedBy("-");
      return;
    }

    setRefundInitiatedBy("-");
    const fullName = (...parts) => parts.filter(Boolean).join(" ").trim();
    const onError = () => setRefundInitiatedBy("-");

    if (refundCreatedBy === REFUND_CREATED_BY.PATIENT) {
      navigoDashboardUtils.fetchPatientId(
        refundCreatedById,
        (res) =>
          setRefundInitiatedBy(
            `${fullName(res?.firstName, res?.lastName)} (Patient)`,
          ),
        onError,
      );
    } else if (refundCreatedBy === REFUND_CREATED_BY.EMPLOYEE) {
      navigoDashboardUtils.getEmployeeById(
        refundCreatedById,
        (res) =>
          setRefundInitiatedBy(
            `${fullName(res?.firstName, res?.lastName)} (Emp)`,
          ),
        onError,
      );
    } else if (refundCreatedBy === REFUND_CREATED_BY.EXPERT) {
      navigoDashboardUtils.getDoctorById(
        refundCreatedById,
        (res) =>
          setRefundInitiatedBy(`${fullName(res?.title, res?.name)} (Expert)`),
        onError,
      );
    }
  }, [refundCreatedBy, refundCreatedById, navigoDashboardUtils]);

  const refreshAptDetailScreenData = () => {
    fetchAppointment();
    resetDashboardReload();
  };

  if (loaderToCheckPaymentStatus) {
    return (
      <div style={cardStyle}>
        <div style={headingStyle}>Payment details</div>
        <br />
        <GlobalSkeletonLoader />
      </div>
    );
  }

  // Sponsored — simple early return
  if (view.isSponsored) {
    return (
      <div style={cardStyle}>
        <div style={headingStyle}>Payment details</div>
        <div style={{ marginTop: "24px" }}>
          <div style={labelStyle}>Total amount</div>
          <div style={valueStyle}>{"Rs 0 (Sponsored)"}</div>
        </div>
      </div>
    );
  }

  // No displayable state at all
  if (
    !view.isPending &&
    !view.isInitiated &&
    !view.isFailed &&
    !view.isComplete
  ) {
    return <></>;
  }

  const paymentAction = view.showGenerateLink ? (
    <Button
      raised
      className="zy-btn-orange"
      onClick={() => setShowPaymentLinkDialog(true)}
    >
      Generate payment link
    </Button>
  ) : view.showPaymentLinkShared ? (
    <Button
      raised
      className="zy-btn-green-success"
      onClick={() => setShowPaymentLinkDialog(true)}
    >
      Payment link shared
    </Button>
  ) : view.showFailureLabel ? (
    <Button raised className="zy-btn-red" disabled>
      {view.failureLabel}
    </Button>
  ) : null;

  return (
    <div style={cardStyle}>
      {/* ── Heading + reload ── */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={headingStyle}>Payment details</div>
        <div style={{ marginLeft: "32px", display: "flex" }}>
          <RefreshButton onRefresh={onRefresh} loading={false} />
        </div>

        {view.showRefundButton && (
          <div style={{ marginLeft: "auto" }}>
            <Button
              raised
              secondary
              onClick={() =>
                openRefundDialog({
                  appointmentObj: appointment,
                  isOnlyRefund: true,
                  onRefresh: refreshAptDetailScreenData,
                })
              }
            >
              Refund
            </Button>
            <RefundDialog
              dialogState={dialogState}
              setDialogState={setDialogState}
              handleClose={handleClose}
            />
          </div>
        )}
      </div>

      {/* ── Zyla order ID ── */}
      <div style={{ marginTop: "24px" }}>
        <div style={labelStyle}>Zyla order ID</div>
        <div style={valueStyle}>{view.paymentOrderId || "-"}</div>
      </div>

      {/* ── Amounts · statuses · payment link action ──
          A grid so the status column of both rows starts at the same x, whatever
          the amounts are. Column 1 = amount, column 2 = status, column 3 = the
          payment action. */}
      <div style={amountsGridStyle}>
        <div>
          <div style={labelStyle}>Total amount</div>
          <div style={valueStyle}>
            {formatCurrency(view.paymentAmount) || "-"}
          </div>
        </div>
        <div>
          {view.paymentStatusDisplay.show && (
            <>
              <div style={labelStyle}>Payment status</div>
              <div
                style={{
                  ...valueStyle,
                  color: view.paymentStatusDisplay.color,
                }}
              >
                {view.paymentStatusDisplay.label}
              </div>
            </>
          )}
        </div>
        {/* 48px column gap + 102px = the 150px gap the design asks for */}
        <div style={{ marginLeft: "102px" }}>{paymentAction}</div>

        {view.hasRefundInfo && (
          <>
            <div>
              <div style={labelStyle}>Total refund amount</div>
              <div style={valueStyle}>
                {formatCurrency(view.refundGrandTotal) || "-"}
              </div>
            </div>
            <div>
              {view.refundStatusDisplay.show && (
                <>
                  <div style={labelStyle}>Refund status</div>
                  <div
                    style={{
                      ...valueStyle,
                      color: view.refundStatusDisplay.color,
                    }}
                  >
                    {view.refundStatusDisplay.label}
                  </div>
                </>
              )}
            </div>
            <div />
          </>
        )}
      </div>

      {/* ── Transactions ── */}
      <TransactionTable
        rows={view.transactionRows}
        refundInitiatedBy={refundInitiatedBy}
      />

      {/* ── Payment link dialog ── */}
      {showPaymentLinkDialog && (
        <PaymentLinkDialog
          visible={showPaymentLinkDialog}
          onClose={() => setShowPaymentLinkDialog(false)}
          appointment={appointment}
          refreshAptDetailScreenData={
            view.isPending ? refreshAptDetailScreenData : undefined
          }
          profileDetails={profileDetails}
        />
      )}
    </div>
  );
};

// Payments and refunds in one table, oldest first. "Refund initiated by" only
// applies to refund rows; payment rows carry a hyphen.
const TransactionTable = ({ rows, refundInitiatedBy }) => {
  // Right rule on every column but the last, bottom rule on every row but the
  // last — the wrapper supplies the outer border.
  const rules = (columnIndex, isLastRow) => ({
    ...(columnIndex < TABLE_HEADERS.length - 1
      ? { borderRight: "1px solid #D9D9D9" }
      : {}),
    ...(isLastRow ? {} : { borderBottom: "1px solid #D9D9D9" }),
  });

  return (
    <div style={tableWrapperStyle}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            {TABLE_HEADERS.map((header, columnIndex) => (
              <th
                key={header}
                style={{ ...headerCellStyle, ...rules(columnIndex, false) }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row, rowIndex) => {
              const isLastRow = rowIndex === rows.length - 1;
              const cells = [
                row.method,
                row.amount,
                row.orderOrRefundId,
                row.paymentId,
                row.transactedAt,
                row.isRefund ? refundInitiatedBy : "-",
                row.status,
              ];
              return (
                <tr key={row.key}>
                  {cells.map((cell, columnIndex) => (
                    <td
                      key={TABLE_HEADERS[columnIndex]}
                      style={{
                        ...cellStyle,
                        ...rules(columnIndex, isLastRow),
                        overflowWrap: "break-word",
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr>
              <td style={cellStyle} colSpan={TABLE_HEADERS.length}>
                No payment data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PaymentAndRefund;
